// Service-worker registration and the update handshake.
//
// The worker never takes over on its own (see public/sw.js): a new version
// installs, precaches, then waits. This module notices that waiting worker
// and hands it to the UI, which asks the user before swapping versions —
// reloading out from under someone mid-way through logging a dose is the
// failure mode this exists to avoid.
//
// It also polls for updates. An installed PWA can stay open for days without
// a single navigation, and browsers only check for a new worker on their own
// schedule, so without this a deployed fix could sit unseen indefinitely.

const CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly while the app is open

// True once the user has accepted an update, so the controllerchange handler
// can tell "the update I asked for" apart from "this page just got its very
// first controller" (which must NOT reload).
let acceptedUpdate = false;
let reloading = false;

// Ask the waiting worker to activate, then reload once it has taken control.
export function applyUpdate(registration) {
  const waiting = registration?.waiting;
  if (!waiting) return;
  acceptedUpdate = true;
  waiting.postMessage({ type: "SKIP_WAITING" });
  // Safety net: if controllerchange never arrives (an edge case on some
  // browsers when the worker was already active), reload anyway so the user
  // isn't left staring at a button that did nothing.
  setTimeout(() => { if (!reloading) { reloading = true; window.location.reload(); } }, 3000);
}

/**
 * Registers the worker and reports when a new version is ready.
 * @param {(registration: ServiceWorkerRegistration) => void} onUpdateReady
 * @returns {() => void} cleanup
 */
export function registerServiceWorker(onUpdateReady) {
  // Checks the object itself, not just the key: some embedded browsers
  // expose a `serviceWorker` property that is null/undefined.
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return () => {};

  let registration = null;
  let interval = null;
  const notify = () => { if (registration?.waiting) onUpdateReady?.(registration); };

  const onControllerChange = () => {
    // Only reload for an update the user actually accepted. On a first-ever
    // registration the controller also changes, and reloading there would be
    // a surprise page refresh on someone's first visit.
    if (!acceptedUpdate || reloading) return;
    reloading = true;
    window.location.reload();
  };
  navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

  const checkForUpdate = () => { registration?.update?.().catch(() => {}); };
  const onVisible = () => { if (document.visibilityState === "visible") checkForUpdate(); };

  navigator.serviceWorker
    .register("/sw.js")
    .then((reg) => {
      registration = reg;
      // A version may already have been waiting from a previous visit.
      notify();
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          // "installed" with an existing controller means this is an update
          // rather than the first install, so it's now sitting in waiting.
          if (installing.state === "installed" && navigator.serviceWorker.controller) notify();
        });
      });
      interval = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("focus", checkForUpdate);
    })
    .catch(() => {});

  return () => {
    if (interval) clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", checkForUpdate);
    navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
  };
}
